
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { API } from '../../utils/api';

export function AdminSettings({ headers }) {
  const [settings, setSettings] = useState({
    global_max_per_month: 50000,
    global_max_per_day: 5000,
    global_max_per_transaction: 2000,
    buddy_type_limits: {
      minor: { daily: 200, monthly: 1500, per_tx: 100 },
      buddy: { daily: 500, monthly: 3000, per_tx: 250 }
    },
    allowed_commercial_swish: []
  });
  const [allowedMerchantText, setAllowedMerchantText] = useState('');
  const [notificationSettings, setNotificationSettings] = useState({
    smtp: {
      host: '',
      port: '',
      username: '',
      sender: '',
      use_tls: '',
      use_ssl: '',
      configured: false
    },
    fcm: {
      server_key: '',
      configured: false
    }
  });

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings`, { headers });
      const buddyLimits = res.data.buddy_type_limits || {};
      if (buddyLimits.junior && !buddyLimits.minor) {
        buddyLimits.minor = buddyLimits.junior;
      }
      setSettings({ ...res.data, buddy_type_limits: buddyLimits });
      setAllowedMerchantText((res.data.allowed_commercial_swish || []).join('\n'));
      const notif = await axios.get(`${API}/admin/notification-settings`, { headers });
      setNotificationSettings(notif.data);
    } catch (e) {
      toast.error('Failed to fetch settings');
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const handleSave = async () => {
    try {
      const payload = {
        ...settings,
        allowed_commercial_swish: allowedMerchantText
          .split(/\n|,/)
          .map(v => v.trim())
          .filter(Boolean)
      };
      await axios.put(`${API}/admin/settings`, payload, { headers });
      toast.success('Settings updated');
    } catch (e) {
      toast.error('Failed to update settings');
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">System Settings</h2>
      
      <Card>
        <CardHeader>
          <CardTitle>Global Budget Limits</CardTitle>
          <CardDescription>Set the maximum limits that payers can assign to Buddys.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 max-w-md">
          <div>
            <Label>Max Monthly Limit (SEK)</Label>
            <Input 
              type="number" 
              value={settings.global_max_per_month} 
              onChange={e => setSettings({...settings, global_max_per_month: parseFloat(e.target.value)})} 
            />
          </div>
          <div>
            <Label>Max Daily Limit (SEK)</Label>
            <Input 
              type="number" 
              value={settings.global_max_per_day} 
              onChange={e => setSettings({...settings, global_max_per_day: parseFloat(e.target.value)})} 
            />
          </div>
          <div>
            <Label>Max Transaction Limit (SEK)</Label>
            <Input 
              type="number" 
              value={settings.global_max_per_transaction} 
              onChange={e => setSettings({...settings, global_max_per_transaction: parseFloat(e.target.value)})} 
            />
          </div>
          <Button onClick={handleSave} data-testid="save-settings">Save Settings</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Buddy Type Limits</CardTitle>
          <CardDescription>Global limits for Junior and Buddy accounts.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {['minor', 'buddy'].map((typeKey) => (
            <div key={typeKey} className="border rounded-md p-3 space-y-2">
              <div className="font-semibold capitalize">{typeKey}</div>
              <div>
                <Label>Daily Limit</Label>
                <Input
                  type="number"
                  value={settings.buddy_type_limits?.[typeKey]?.daily || 0}
                  onChange={(e) => setSettings({
                    ...settings,
                    buddy_type_limits: {
                      ...settings.buddy_type_limits,
                      [typeKey]: {
                        ...settings.buddy_type_limits?.[typeKey],
                        daily: Number(e.target.value)
                      }
                    }
                  })}
                  data-testid={`${typeKey}-daily-limit`}
                />
              </div>
              <div>
                <Label>Monthly Limit</Label>
                <Input
                  type="number"
                  value={settings.buddy_type_limits?.[typeKey]?.monthly || 0}
                  onChange={(e) => setSettings({
                    ...settings,
                    buddy_type_limits: {
                      ...settings.buddy_type_limits,
                      [typeKey]: {
                        ...settings.buddy_type_limits?.[typeKey],
                        monthly: Number(e.target.value)
                      }
                    }
                  })}
                  data-testid={`${typeKey}-monthly-limit`}
                />
              </div>
              <div>
                <Label>Per Transaction Limit</Label>
                <Input
                  type="number"
                  value={settings.buddy_type_limits?.[typeKey]?.per_tx || 0}
                  onChange={(e) => setSettings({
                    ...settings,
                    buddy_type_limits: {
                      ...settings.buddy_type_limits,
                      [typeKey]: {
                        ...settings.buddy_type_limits?.[typeKey],
                        per_tx: Number(e.target.value)
                      }
                    }
                  })}
                  data-testid={`${typeKey}-per-tx-limit`}
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Commercial Swish Allowlist</CardTitle>
          <CardDescription>Only these Swish numbers can be used in Buddy requests.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <textarea
            className="w-full min-h-[140px] border rounded-md p-2 text-sm"
            placeholder="Enter one Swish number per line"
            value={allowedMerchantText}
            onChange={(e) => setAllowedMerchantText(e.target.value)}
            data-testid="commercial-swish-allowlist"
          />
          <Button onClick={handleSave} data-testid="save-allowlist">Save Allowlist</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Notifications & Email</CardTitle>
          <CardDescription>
            Credentials are read from environment variables. Update values in deployment settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 max-w-md">
          <div>
            <Label>SMTP Host</Label>
            <Input value={notificationSettings.smtp.host} disabled data-testid="smtp-host" />
          </div>
          <div>
            <Label>SMTP Port</Label>
            <Input value={notificationSettings.smtp.port} disabled data-testid="smtp-port" />
          </div>
          <div>
            <Label>SMTP Username</Label>
            <Input value={notificationSettings.smtp.username} disabled data-testid="smtp-username" />
          </div>
          <div>
            <Label>SMTP Sender</Label>
            <Input value={notificationSettings.smtp.sender} disabled data-testid="smtp-sender" />
          </div>
          <div>
            <Label>SMTP TLS</Label>
            <Input value={String(notificationSettings.smtp.use_tls)} disabled data-testid="smtp-use-tls" />
          </div>
          <div>
            <Label>SMTP SSL</Label>
            <Input value={String(notificationSettings.smtp.use_ssl)} disabled data-testid="smtp-use-ssl" />
          </div>
          <div className="text-xs text-muted-foreground">
            Status: {notificationSettings.smtp.configured ? 'Configured' : 'Not configured'}
          </div>

          <div className="pt-2">
            <Label>FCM Server Key</Label>
            <Input value={notificationSettings.fcm.server_key} disabled data-testid="fcm-server-key" />
          </div>
          <div className="text-xs text-muted-foreground">
            Status: {notificationSettings.fcm.configured ? 'Configured' : 'Not configured'}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
